import type { ExpenseCategory, ExpenseMethod, ExpenseStatus } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  Input,
  KpiCard,
  NativeSelect,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@horevia/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Banknote,
  Coins,
  Download,
  Landmark,
  List,
  Plus,
  Search,
  Smartphone,
  Tags,
  Wallet,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PERIOD_PRESETS,
  resolvePeriod,
  todayIsoDate,
  type PeriodPreset,
  type PeriodRange,
} from '@/features/analytics/period';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { useSession } from '@/features/auth/session';
import { ExpenseDrawer } from '@/features/expenses/ExpenseDrawer';
import {
  downloadExpensesCsv,
  expensesQueryOptions,
  expensesSummaryQueryOptions,
} from '@/features/expenses/queries';
import {
  EXPENSE_CATEGORIES,
  EXPENSE_METHODS,
  EXPENSE_STATUSES,
  expenseMethodTone,
  expenseStatusTone,
} from '@/features/expenses/status';
import { openDownloadedFile } from '@/lib/api';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort } from '@/lib/format';
import { useScopedPropertyId } from '@/lib/property';

interface ExpensesSearch {
  q?: string;
  method?: ExpenseMethod;
  category?: ExpenseCategory;
  status?: ExpenseStatus;
  period?: PeriodPreset;
  from?: string;
  to?: string;
  /** Ouvre le tiroir « Nouvelle dépense » (⌘K, liens externes). */
  new?: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute('/_app/finance/expenses/')({
  validateSearch: (search: Record<string, unknown>): ExpensesSearch => ({
    ...(typeof search['q'] === 'string' && search['q'] ? { q: search['q'] } : {}),
    ...(EXPENSE_METHODS.includes(search['method'] as ExpenseMethod)
      ? { method: search['method'] as ExpenseMethod }
      : {}),
    ...(EXPENSE_CATEGORIES.includes(search['category'] as ExpenseCategory)
      ? { category: search['category'] as ExpenseCategory }
      : {}),
    ...(EXPENSE_STATUSES.includes(search['status'] as ExpenseStatus)
      ? { status: search['status'] as ExpenseStatus }
      : {}),
    ...(PERIOD_PRESETS.includes(search['period'] as PeriodPreset)
      ? { period: search['period'] as PeriodPreset }
      : {}),
    ...(typeof search['from'] === 'string' && ISO_DATE.test(search['from'])
      ? { from: search['from'] }
      : {}),
    ...(typeof search['to'] === 'string' && ISO_DATE.test(search['to'])
      ? { to: search['to'] }
      : {}),
    ...(search['new'] === 'expense' ? { new: 'expense' } : {}),
  }),
  component: ExpensesPage,
});

/**
 * Suivi des dépenses (D-052) : période, bandeau KPI (`GET /expenses/summary`),
 * filtres moyen / catégorie / statut / recherche, tableau paginé par curseur,
 * export CSV (`GET /reports/EXPENSES?format=csv`, `report.export`) et tiroir
 * « Nouvelle dépense » (`expense.record`).
 */
function ExpensesPage() {
  const { t, i18n } = useTranslation(['expenses', 'cash', 'analytics', 'common', 'errors']);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const session = useSession();
  const canRead = session.can('expense.read');
  const canRecord = session.can('expense.record');
  const canExport = session.can('report.export');
  const canReadCash = session.can('cash.read');

  const today = todayIsoDate(session.user.tenant.timezone);
  const preset: PeriodPreset = search.period ?? 'THIS_MONTH';
  const custom: PeriodRange = { from: search.from ?? today, to: search.to ?? today };
  const range = resolvePeriod(preset, today, custom);

  const [term, setTerm] = useState(search.q ?? '');
  const [debounced, setDebounced] = useState(search.q ?? '');
  const [drawerOpen, setDrawerOpen] = useState(search.new === 'expense');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.length >= 2 ? term : ''), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  // ⌘K « Nouvelle dépense » alors que la page est déjà affichée.
  useEffect(() => {
    if (search.new === 'expense') setDrawerOpen(true);
  }, [search.new]);

  const setSearch = (next: Partial<ExpensesSearch>) =>
    void navigate({
      search: (current) => {
        // Une valeur `undefined` retire le paramètre de l'URL au lieu d'y laisser « ?method= ».
        const merged: Record<string, unknown> = { ...current, ...next };
        for (const key of Object.keys(merged)) if (merged[key] === undefined) delete merged[key];
        return merged;
      },
      replace: true,
    });

  const scopedPropertyId = useScopedPropertyId();
  const filters = {
    from: range.from,
    to: range.to,
    ...(scopedPropertyId ? { propertyId: scopedPropertyId } : {}),
    ...(debounced ? { q: debounced } : {}),
    ...(search.method ? { method: search.method } : {}),
    ...(search.category ? { category: search.category } : {}),
    ...(search.status ? { status: search.status } : {}),
  };

  const query = useInfiniteQuery(expensesQueryOptions(filters, 25, canRead));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);
  const summaryQuery = useQuery(expensesSummaryQueryOptions(range, canRead));

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const summary = summaryQuery.data;
  const currency = summary?.currencyCode ?? 'XOF';
  const sumMethods = (predicate: (method: ExpenseMethod) => boolean) =>
    (summary?.byMethod ?? [])
      .filter((entry) => predicate(entry.method))
      .reduce((sum, entry) => sum + entry.amount, 0);
  const cashTotal = sumMethods((method) => method === 'CASH');
  const mobileTotal = sumMethods((method) => method === 'MOBILE_MONEY');
  const bankTotal = sumMethods((method) =>
    ['CHEQUE', 'BANK_TRANSFER', 'BANK_WITHDRAWAL'].includes(method),
  );
  const methodMax = Math.max(1, ...(summary?.byMethod ?? []).map((entry) => entry.amount));
  const categoryMax = Math.max(1, ...(summary?.byCategory ?? []).map((entry) => entry.amount));

  const hasFilters =
    debounced !== '' ||
    Boolean(search.method) ||
    Boolean(search.category) ||
    Boolean(search.status);

  const shownTotal = rows
    .filter((row) => row.status !== 'CANCELLED')
    .reduce((sum, row) => sum + row.amount, 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      const file = await downloadExpensesCsv(range);
      openDownloadedFile(file);
      toast.success(t('expenses:toasts.exported', { name: file.filename }));
    } catch (error) {
      toast.error(errorMessage(error, t));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container>
      <PageHeader
        title={t('expenses:title')}
        subtitle={t('expenses:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('expenses:title') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            {canReadCash ? (
              <Button variant="secondary" asChild>
                <Link to="/billing/cash">
                  <Wallet className="ico" aria-hidden="true" />
                  {t('cash:title')}
                </Link>
              </Button>
            ) : null}
            {canExport ? (
              <Button
                variant="secondary"
                loading={exporting}
                disabled={rows.length === 0}
                onClick={() => void handleExport()}
              >
                <Download className="ico" aria-hidden="true" />
                {t('expenses:actions.exportCsv')}
              </Button>
            ) : null}
            {canRecord ? (
              <Button variant="primary" onClick={() => setDrawerOpen(true)}>
                <Plus className="ico" aria-hidden="true" />
                {t('expenses:actions.record')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="stack">
        <Card>
          <CardBody>
            <PeriodPicker
              preset={preset}
              onPresetChange={(value) => setSearch({ period: value })}
              range={range}
              custom={custom}
              onCustomChange={(value) => setSearch({ from: value.from, to: value.to })}
              hideCompare
            />
          </CardBody>
        </Card>

        <div className="grid grid-4">
          <KpiCard
            tone="red"
            icon={<Coins className="ico ico-16" aria-hidden="true" />}
            label={t('expenses:kpi.total')}
            value={formatAmount(summary?.total ?? 0, i18n.language, currency)}
            loading={summaryQuery.isPending}
            footer={t('expenses:kpi.totalFoot', { count: summary?.count ?? 0 })}
          />
          <KpiCard
            tone="green"
            icon={<Banknote className="ico ico-16" aria-hidden="true" />}
            label={t('expenses:kpi.cash')}
            value={formatAmount(cashTotal, i18n.language, currency)}
            loading={summaryQuery.isPending}
            footer={t('expenses:method.CASH')}
          />
          <KpiCard
            tone="amber"
            icon={<Smartphone className="ico ico-16" aria-hidden="true" />}
            label={t('expenses:kpi.mobileMoney')}
            value={formatAmount(mobileTotal, i18n.language, currency)}
            loading={summaryQuery.isPending}
            footer={(summary?.byMethod ?? [])
              .filter((entry) => entry.method === 'MOBILE_MONEY' && entry.provider)
              .map(
                (entry) =>
                  `${t(`expenses:provider.${entry.provider ?? 'OTHER'}`)} · ${formatAmount(entry.amount, i18n.language)}`,
              )
              .join(' — ')}
          />
          <KpiCard
            tone="blue"
            icon={<Landmark className="ico ico-16" aria-hidden="true" />}
            label={t('expenses:kpi.bank')}
            value={formatAmount(bankTotal, i18n.language, currency)}
            loading={summaryQuery.isPending}
            footer={(summary?.byMethod ?? [])
              .filter((entry) =>
                ['CHEQUE', 'BANK_TRANSFER', 'BANK_WITHDRAWAL'].includes(entry.method),
              )
              .map(
                (entry) =>
                  `${t(`expenses:method.${entry.method}`)} · ${formatAmount(entry.amount, i18n.language)}`,
              )
              .join(' — ')}
          />
        </div>

        {summary && (summary.byMethod.length > 0 || summary.byCategory.length > 0) ? (
          <div className="grid grid-2">
            <Card>
              <CardHeader
                title={t('expenses:kpi.byMethodTitle')}
                icon={<Coins className="ico ico-16" aria-hidden="true" />}
                iconTone="amber"
                actions={<span className="t-12 muted">{t('expenses:kpi.byFoot')}</span>}
              />
              <CardBody>
                <div className="hbars">
                  {[...summary.byMethod]
                    .sort((a, b) => b.amount - a.amount)
                    .map((entry) => (
                      <div className="hbar" key={`${entry.method}-${entry.provider ?? ''}`}>
                        <span className="hbar__label">
                          {entry.provider
                            ? t(`expenses:provider.${entry.provider}`)
                            : t(`expenses:method.${entry.method}`)}
                        </span>
                        <span className="hbar__track">
                          <span
                            className="hbar__fill"
                            style={{
                              width: `${Math.max((entry.amount / methodMax) * 100, entry.amount > 0 ? 2 : 0)}%`,
                            }}
                          />
                        </span>
                        <span className="hbar__value">
                          {formatAmount(entry.amount, i18n.language, currency)}
                        </span>
                        <span className="hbar__sub">
                          {t('expenses:kpi.count', { count: entry.count })}
                        </span>
                      </div>
                    ))}
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardHeader
                title={t('expenses:kpi.byCategoryTitle')}
                icon={<Tags className="ico ico-16" aria-hidden="true" />}
                iconTone="violet"
              />
              <CardBody>
                <div className="hbars">
                  {[...summary.byCategory]
                    .sort((a, b) => b.amount - a.amount)
                    .map((entry) => (
                      <div className="hbar" key={entry.category}>
                        <span className="hbar__label">
                          {t(`expenses:category.${entry.category}`)}
                        </span>
                        <span className="hbar__track">
                          <span
                            className="hbar__fill"
                            style={{
                              width: `${Math.max((entry.amount / categoryMax) * 100, entry.amount > 0 ? 2 : 0)}%`,
                            }}
                          />
                        </span>
                        <span className="hbar__value">
                          {formatAmount(entry.amount, i18n.language, currency)}
                        </span>
                        <span className="hbar__sub">
                          {t('expenses:kpi.count', { count: entry.count })}
                        </span>
                      </div>
                    ))}
                </div>
              </CardBody>
            </Card>
          </div>
        ) : null}

        <Card>
          <CardHeader
            title={t('expenses:list.tableTitle')}
            icon={<List className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
            actions={
              <Badge tone="neutral">{t('expenses:list.countLabel', { count: rows.length })}</Badge>
            }
          />

          <Toolbar>
            <Input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('expenses:list.searchPlaceholder')}
              aria-label={t('expenses:list.searchLabel')}
              icon={<Search className="ico" aria-hidden="true" />}
              style={{ width: 300, height: 32, fontSize: 'var(--fs-13)' }}
            />
            {EXPENSE_METHODS.map((method) => (
              <ChipFilter
                key={method}
                active={search.method === method}
                onClick={() => setSearch({ method: search.method === method ? undefined : method })}
              >
                {t(`expenses:method.${method}`)}
              </ChipFilter>
            ))}
          </Toolbar>

          <Toolbar>
            <NativeSelect
              aria-label={t('expenses:list.filters.category')}
              value={search.category ?? ''}
              onChange={(event) =>
                setSearch({
                  category: event.target.value
                    ? (event.target.value as ExpenseCategory)
                    : undefined,
                })
              }
              style={{ width: 220, height: 32, fontSize: 'var(--fs-13)' }}
            >
              <option value="">{t('expenses:list.filters.allCategories')}</option>
              {EXPENSE_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {t(`expenses:category.${category}`)}
                </option>
              ))}
            </NativeSelect>
            {EXPENSE_STATUSES.map((status) => (
              <ChipFilter
                key={status}
                active={search.status === status}
                onClick={() => setSearch({ status: search.status === status ? undefined : status })}
              >
                {t(`expenses:status.${status}`)}
              </ChipFilter>
            ))}
            <span className="spacer" />
            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTerm('');
                  setDebounced('');
                  setSearch({
                    q: undefined,
                    method: undefined,
                    category: undefined,
                    status: undefined,
                  });
                }}
              >
                {t('common:actions.clearFilters')}
              </Button>
            ) : null}
          </Toolbar>

          <CardBody density="flush">
            {query.isPending ? (
              <TableSkeleton rows={6} columns={8} label={t('common:states.loading')} />
            ) : query.isError ? (
              <ErrorState
                title={t('common:states.errorTitle')}
                description={errorMessage(query.error, t)}
                traceId={errorTraceId(query.error)}
                retryLabel={t('common:actions.retry')}
                onRetry={() => void query.refetch()}
                traceCopyLabel={t('common:actions.copy')}
                traceCopiedLabel={t('common:actions.copied')}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<Coins className="ico ico-24" aria-hidden="true" />}
                title={
                  hasFilters ? t('expenses:list.emptySearchTitle') : t('expenses:list.emptyTitle')
                }
                description={
                  hasFilters ? t('expenses:list.emptySearchBody') : t('expenses:list.emptyBody')
                }
              />
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('expenses:columns.reference')}</Th>
                      <Th>{t('expenses:columns.date')}</Th>
                      <Th>{t('expenses:columns.beneficiary')}</Th>
                      <Th>{t('expenses:columns.category')}</Th>
                      <Th>{t('expenses:columns.method')}</Th>
                      <Th align="right">{t('expenses:columns.amount')}</Th>
                      <Th>{t('expenses:columns.status')}</Th>
                      <Th>{t('expenses:columns.recordedBy')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <Tr key={row.id}>
                        <Td>
                          <Link to="/finance/expenses/$expenseId" params={{ expenseId: row.id }}>
                            <span className="cell-id">{row.reference}</span>
                          </Link>
                        </Td>
                        <Td className="t-13" numeric>
                          {formatDateShort(`${row.expenseDate}T00:00:00.000Z`, i18n.language)}
                        </Td>
                        <Td className="t-13">
                          {row.beneficiary ?? (
                            <span className="muted">{t('expenses:detail.noBeneficiary')}</span>
                          )}
                          <div className="t-12 muted truncate" style={{ maxWidth: 320 }}>
                            {row.description}
                          </div>
                        </Td>
                        <Td className="t-13">{t(`expenses:category.${row.category}`)}</Td>
                        <Td>
                          <Badge tone={expenseMethodTone(row.method, row.provider)}>
                            {row.provider
                              ? t(`expenses:provider.${row.provider}`)
                              : t(`expenses:method.${row.method}`)}
                          </Badge>
                          {row.transactionReference ? (
                            <div className="t-12 muted mono">{row.transactionReference}</div>
                          ) : null}
                        </Td>
                        <Td align="right" numeric>
                          <span
                            className="strong"
                            style={{
                              textDecoration:
                                row.status === 'CANCELLED' ? 'line-through' : undefined,
                            }}
                          >
                            {formatAmount(row.amount, i18n.language)}
                          </span>
                        </Td>
                        <Td>
                          <Badge tone={expenseStatusTone(row.status)} dot>
                            {t(`expenses:status.${row.status}`)}
                          </Badge>
                        </Td>
                        <Td className="t-13">{row.recordedBy.fullName}</Td>
                      </Tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <Td colSpan={5} align="right" className="t-13">
                        {t('expenses:list.periodTotal')}
                      </Td>
                      <Td align="right" numeric strong>
                        {formatAmount(shownTotal, i18n.language)}
                      </Td>
                      <Td colSpan={2} />
                    </tr>
                  </tfoot>
                </Table>
              </TableWrap>
            )}
          </CardBody>

          {query.hasNextPage ? (
            <div className="card__foot">
              <Button
                variant="secondary"
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common:pagination.loadMore')}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>

      <ExpenseDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </Container>
  );
}
